import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { ChatbotV2, MarkdownText } from '@amzn/aws-tc-chatbot-ui';
import { Avatar, ChatBubble } from '@amzn/awsui-chat-components';
import {
    Box,
    Button,
    Icon,
    Link,
    SpaceBetween,
} from '@amzn/awsui-components-react-v3';
import {
    ApolloClient,
    ApolloLink,
    ApolloProvider,
    HttpLink,
    InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { selectFeatures } from '../../../../../common/store/slices/featureSlice';
import {
    decodeSchedulingAssistantMessageContent,
    findSchedulingAssistantConversation,
    getSchedulingAssistantAuthHeaders,
    getSchedulingAssistantExternalReferenceId,
    getSchedulingAssistantRealtimeUrl,
    getSchedulingAssistantTarget,
    inferSchedulingAssistantRecommendationCount,
    isSchedulingAssistantConversationExpired,
    SchedulingAssistantTarget,
    SCHEDULING_ASSISTANT_AGENT_NAME,
    SCHEDULING_ASSISTANT_EXTERNAL_REFERENCE,
} from '../../../../../common/utils/schedulingAssistant';
import {
    getSupergraphAuthHeaders,
    getSupergraphEndpoint,
} from '../../../../../common/utils/handleSupergraphRequest';

/**
 * Asks the agent for the first-pass recommendations shown when the modal opens.
 * The agent recognises this prefix and returns the overview as Markdown.
 */
const OVERVIEW_SENTINEL = '__GRIMSBY_SCHEDULING_OVERVIEW_V1__:';
const GUARDRAIL_BLOCKED_MESSAGE = "I'm sorry, I can't help with that request.";
const CHATBOT_ERROR_MESSAGE =
    'The scheduling assistant could not complete that request. Please try again.';
const getErrorMessage = (error: unknown): string =>
    error instanceof Error ? error.message : String(error);
const isGuardrailBlockedError = (error: unknown): boolean =>
    getErrorMessage(error).includes('[GUARDRAIL_BLOCKED]');

type OverviewResponse = {
    message: string;
    recommendationCount: number;
    error?: boolean;
};

type OverviewRequest = {
    activityId?: string;
};

const useSchedulingAssistantTarget = (): SchedulingAssistantTarget | null => {
    const featureFlags = useSelector(selectFeatures);

    return getSchedulingAssistantTarget(featureFlags);
};

const renderCollapsedSummary = (overview: OverviewResponse): string => {
    if (overview.error) {
        return overview.message;
    }

    if (overview.recommendationCount === 0) {
        return 'No Recommendations Found';
    }

    return `Found ${overview.recommendationCount} Instructor Recommendation${
        overview.recommendationCount === 1 ? '' : 's'
    }!`;
};

const withoutRecommendedInstructorsHeading = (message: string): string =>
    message.replace(
        /^[ \t]*Recommended instructors:[ \t]*(?:\r?\n[ \t]*)*/im,
        '',
    );

const formatGeneratedAt = (value?: string): string | undefined => {
    if (!value) return undefined;

    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? undefined
        : date.toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
          });
};

type OverviewTransportResponse = {
    message: string;
    conversationId?: string;
};

export const sanitizeSchedulingAssistantConversationHistory = (
    messages: Array<{ owner: string; content: string }>,
) => {
    let overviewFound = false;

    return messages.flatMap((message) => {
        const decodedMessage = {
            ...message,
            content: decodeSchedulingAssistantMessageContent(message.content),
        };
        if (
            decodedMessage.owner === 'USER' &&
            decodedMessage.content.includes(OVERVIEW_SENTINEL)
        ) {
            return [];
        }

        if (
            decodedMessage.owner === 'BOT' &&
            decodedMessage.content.includes('Recommended instructors:')
        ) {
            if (overviewFound) return [];
            overviewFound = true;
        }

        return [decodedMessage];
    });
};

const createOverviewRequestBody = (
    message: string,
    externalReferenceId?: string,
) => {
    const id = Date.now();

    return {
        thread_id: `overview-${id}`,
        run_id: `run-${id}`,
        state: null,
        messages: [{ id: 'm1', role: 'user', content: message }],
        tools: [],
        context: externalReferenceId
            ? [
                  {
                      description: SCHEDULING_ASSISTANT_EXTERNAL_REFERENCE,
                      value: externalReferenceId,
                  },
              ]
            : [],
        forwarded_props: {
            agent_name: SCHEDULING_ASSISTANT_AGENT_NAME,
        },
    };
};

const readOverviewResponse = async (
    res: Response,
    onTextDelta?: (delta: string) => void,
): Promise<OverviewTransportResponse> => {
    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let out = '';

    const processFrame = (frame: string) => {
        const line = frame
            .split(/\r?\n/)
            .find((value) => value.startsWith('data:'));
        if (!line) return;
        let event;
        try {
            event = JSON.parse(line.slice('data:'.length).replace(/^ /, ''));
        } catch {
            // Ignore non-JSON keepalive frames.
            return;
        }
        if (event?.type === 'RUN_ERROR') {
            throw new Error(event.message || 'Agent run failed');
        }
        if (event?.type === 'TEXT_MESSAGE_CONTENT' && event.delta) {
            out += event.delta;
            onTextDelta?.(event.delta);
        }
    };

    for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split(/\r?\n\r?\n/);
        buf = frames.pop() ?? '';
        frames.forEach(processFrame);
    }

    buf += decoder.decode();
    buf.split(/\r?\n\r?\n/).forEach(processFrame);

    if (!out.trim()) {
        throw new Error('Agent returned no overview content');
    }

    return {
        message: out,
        conversationId:
            res.headers?.get(
                'X-Amzn-Bedrock-AgentCore-Runtime-Custom-Conversation-Id',
            ) ?? undefined,
    };
};

const sendOverviewRequest = async (
    url: string,
    message: string,
    headers?: Record<string, string>,
    externalReferenceId?: string,
    signal?: AbortSignal,
    onTextDelta?: (delta: string) => void,
    onConnected?: () => void,
): Promise<OverviewTransportResponse> => {
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...headers,
        },
        body: JSON.stringify(
            createOverviewRequestBody(message, externalReferenceId),
        ),
        signal,
    });

    if (!res.ok || !res.body) {
        throw new Error(`[agent error ${res.status}] ${await res.text()}`);
    }

    onConnected?.();
    return readOverviewResponse(res, onTextDelta);
};

const sendToGateway = async (
    target: SchedulingAssistantTarget,
    message: string,
    externalReferenceId: string,
    conversationId?: string,
    signal?: AbortSignal,
    onTextDelta?: (delta: string) => void,
    onConnected?: () => void,
): Promise<OverviewTransportResponse> =>
    sendOverviewRequest(
        getSchedulingAssistantRealtimeUrl(target),
        message,
        {
            ...(await getSchedulingAssistantAuthHeaders()),
            ...(conversationId
                ? {
                      'X-Amzn-Bedrock-AgentCore-Runtime-Custom-Conversation-Id':
                          conversationId,
                  }
                : {}),
        },
        externalReferenceId,
        signal,
        onTextDelta,
        onConnected,
    );

const useSchedulingAssistantConversation = (
    target: SchedulingAssistantTarget | null,
    activityId?: string,
) => {
    const [lookup, setLookup] = useState<{
        loaded: boolean;
        target: SchedulingAssistantTarget | null;
        activityId?: string;
        restoredOverview?: OverviewResponse;
        generatedAt?: string;
        refreshVersion: number;
    }>({
        loaded: !target || target === 'local',
        target,
        activityId,
        refreshVersion: 0,
    });
    const [conversationId, setConversationId] = useState<string>();

    useEffect(() => {
        let isCurrent = true;
        const controller = new AbortController();
        setConversationId(undefined);

        // Local development always starts a new conversation without a
        // Supergraph lookup.
        if (!target || target === 'local' || !activityId) {
            setLookup({ loaded: true, target, activityId, refreshVersion: 0 });
            return () => controller.abort();
        }

        setLookup({ loaded: false, target, activityId, refreshVersion: 0 });
        findSchedulingAssistantConversation(activityId, controller.signal)
            .then((existingConversation) => {
                if (!isCurrent) return;
                const isExpired =
                    !!existingConversation &&
                    isSchedulingAssistantConversationExpired(
                        existingConversation,
                    );
                if (!existingConversation || isExpired) {
                    setLookup({
                        loaded: true,
                        target,
                        activityId,
                        refreshVersion: 0,
                    });
                    return;
                }
                const restoredOverviewMessage =
                    existingConversation.messages?.nodes
                        .filter((message) => message.owner === 'BOT')
                        .find((message) =>
                            message.content.includes(
                                'Recommended instructors:',
                            ),
                        )?.content;
                setConversationId(existingConversation.id);
                setLookup({
                    loaded: true,
                    target,
                    activityId,
                    generatedAt:
                        existingConversation.creationTimestamp ?? undefined,
                    restoredOverview: restoredOverviewMessage
                        ? {
                              message: restoredOverviewMessage,
                              recommendationCount:
                                  inferSchedulingAssistantRecommendationCount(
                                      restoredOverviewMessage,
                                  ),
                          }
                        : undefined,
                    refreshVersion: 0,
                });
            })
            .catch((error) => {
                if (error?.name === 'AbortError') return;
                if (isCurrent) {
                    setLookup({
                        loaded: true,
                        target,
                        activityId,
                        refreshVersion: 0,
                    });
                }
            });

        return () => {
            isCurrent = false;
            controller.abort();
        };
    }, [target, activityId]);

    return {
        ...lookup,
        loaded: lookup.loaded && lookup.target === target,
        activeConversationId: conversationId,
        setConversationId,
        setCreatedConversationId: (newConversationId: string) => {
            setConversationId(newConversationId);
            setLookup((current) => ({
                ...current,
                generatedAt: new Date().toISOString(),
            }));
        },
        refreshConversation: () => {
            setConversationId(undefined);
            setLookup((current) => ({
                loaded: true,
                target: current.target,
                activityId: current.activityId,
                refreshVersion: current.refreshVersion + 1,
            }));
        },
    };
};

/**
 * Sends the overview sentinel as the conversation's first user turn and shows
 * the assistant response as the opening recommendations.
 */
const useInstructorOverview = (
    target: SchedulingAssistantTarget | null,
    overviewRequest?: OverviewRequest,
    conversationLoaded = true,
    conversationActivityId?: string,
    conversationId?: string,
    onConversationIdChange?: (conversationId: string) => void,
    restoredOverview?: OverviewResponse,
    refreshVersion = 0,
): {
    overview: OverviewResponse | null;
    estimatedTokensGenerated: number;
    connectionEstablished: boolean;
} => {
    const [overview, setOverview] = useState<OverviewResponse | null>(null);
    const [generatedCharacterCount, setGeneratedCharacterCount] = useState(0);
    const [connectionEstablished, setConnectionEstablished] = useState(false);
    const requestKey = overviewRequest?.activityId;
    const conversationIdRef = useRef(conversationId);
    const onConversationIdChangeRef = useRef(onConversationIdChange);
    conversationIdRef.current = conversationId;
    onConversationIdChangeRef.current = onConversationIdChange;

    useEffect(() => {
        if (
            !target ||
            !overviewRequest?.activityId ||
            !conversationLoaded ||
            conversationActivityId !== overviewRequest.activityId
        ) {
            return undefined;
        }

        if (restoredOverview) {
            setOverview(restoredOverview);
            return undefined;
        }

        let isCurrent = true;
        const controller = new AbortController();
        const externalReferenceId = getSchedulingAssistantExternalReferenceId(
            overviewRequest.activityId,
        );
        setOverview(null);
        setGeneratedCharacterCount(0);
        setConnectionEstablished(false);

        sendToGateway(
            target,
            `${OVERVIEW_SENTINEL} activityId=${overviewRequest.activityId}`,
            externalReferenceId,
            conversationIdRef.current,
            controller.signal,
            (delta) => {
                if (isCurrent) {
                    setGeneratedCharacterCount(
                        (currentCount) => currentCount + delta.length,
                    );
                }
            },
            () => {
                if (isCurrent) setConnectionEstablished(true);
            },
        )
            .then((response) => {
                if (!isCurrent) return;
                if (response.conversationId) {
                    onConversationIdChangeRef.current?.(
                        response.conversationId,
                    );
                }
                setOverview({
                    message: response.message,
                    recommendationCount:
                        inferSchedulingAssistantRecommendationCount(
                            response.message,
                        ),
                });
            })
            .catch((error) => {
                if (error?.name === 'AbortError') return;
                if (isCurrent) {
                    setOverview({
                        message: 'Could not load the instructor overview.',
                        recommendationCount: 0,
                        error: true,
                    });
                }
            });

        return () => {
            isCurrent = false;
            controller.abort();
        };
    }, [
        target,
        requestKey,
        conversationLoaded,
        conversationActivityId,
        restoredOverview,
        refreshVersion,
    ]);

    return {
        overview,
        estimatedTokensGenerated: Math.ceil(generatedCharacterCount / 4),
        connectionEstablished,
    };
};

/**
 * The chat surface itself: a full-height sidebar. Rendered through a portal
 * because the panel is `position: fixed` and has to sit above the Assign
 * Instructor modal, which owns a lower stacking context.
 */
const SchedulingAssistantSidebar = ({
    target,
    activityId,
    conversationId,
    onConversationIdChange,
    onClose,
    welcomeMessage,
}: {
    target: SchedulingAssistantTarget;
    activityId?: string;
    conversationId?: string;
    onConversationIdChange: (conversationId: string) => void;
    onClose: () => void;
    welcomeMessage?: string;
}) => {
    const handledChatbotErrorsRef = useRef(new WeakSet<object>());

    useEffect(() => {
        const isHandledChatbotError = (error: unknown) => {
            if (isGuardrailBlockedError(error)) return true;
            if (
                typeof error === 'object' &&
                error !== null &&
                handledChatbotErrorsRef.current.has(error)
            ) {
                return true;
            }
            return false;
        };
        const suppressChatbotError = (event: ErrorEvent) => {
            if (
                !isHandledChatbotError(event.error) &&
                !isHandledChatbotError(event.message)
            )
                return;
            event.preventDefault();
            event.stopImmediatePropagation();
        };
        const suppressChatbotRejection = (event: PromiseRejectionEvent) => {
            if (!isHandledChatbotError(event.reason)) return;
            event.preventDefault();
            event.stopImmediatePropagation();
        };

        window.addEventListener('error', suppressChatbotError, true);
        window.addEventListener(
            'unhandledrejection',
            suppressChatbotRejection,
            true,
        );
        return () => {
            window.removeEventListener('error', suppressChatbotError, true);
            window.removeEventListener(
                'unhandledrejection',
                suppressChatbotRejection,
                true,
            );
        };
    }, []);

    const apolloClient = useMemo(() => {
        const historyLink = new ApolloLink((operation, forward) =>
            forward(operation).map((result) => {
                const conversation = result.data?.conversationById as
                    | {
                          messages?: {
                              nodes?: Array<{
                                  owner: string;
                                  content: string;
                              }>;
                          };
                      }
                    | undefined;
                const messages = conversation?.messages?.nodes;
                if (!messages) return result;

                return {
                    ...result,
                    data: {
                        ...result.data,
                        conversationById: {
                            ...conversation,
                            messages: {
                                ...conversation.messages,
                                nodes: sanitizeSchedulingAssistantConversationHistory(
                                    messages,
                                ),
                            },
                        },
                    },
                };
            }),
        );

        return new ApolloClient({
            cache: new InMemoryCache(),
            link: setContext(async (_, { headers }) => ({
                headers: {
                    ...headers,
                    ...(await getSupergraphAuthHeaders()),
                },
            }))
                .concat(historyLink)
                .concat(new HttpLink({ uri: getSupergraphEndpoint() })),
        });
    }, []);
    const chatbot = (
        <ChatbotV2
            config={{
                mode: 'sidebar',
                header: { title: 'Scheduling assistant' },
                welcomeMessage: target === 'local' ? welcomeMessage : '',
            }}
            enableMarkdown
            onClose={onClose}
            onError={(error) => {
                handledChatbotErrorsRef.current.add(error);
            }}
            renderError={({ error }) => (
                <Box color="text-status-error">
                    {isGuardrailBlockedError(error)
                        ? GUARDRAIL_BLOCKED_MESSAGE
                        : CHATBOT_ERROR_MESSAGE}
                </Box>
            )}
            endpoint={getSchedulingAssistantRealtimeUrl(target)}
            agentName={SCHEDULING_ASSISTANT_AGENT_NAME}
            getAuthHeaders={getSchedulingAssistantAuthHeaders}
            conversationId={conversationId}
            onConversationIdChange={onConversationIdChange}
            context={
                activityId
                    ? [
                          {
                              description:
                                  SCHEDULING_ASSISTANT_EXTERNAL_REFERENCE,
                              value: getSchedulingAssistantExternalReferenceId(
                                  activityId,
                              ),
                          },
                      ]
                    : []
            }
        />
    );

    const sidebar = (
        <div
            style={{ position: 'relative', zIndex: 5001 }}
            data-testid="SchedulingAssistantSidebar"
        >
            {target !== 'local' ? (
                <ApolloProvider client={apolloClient}>{chatbot}</ApolloProvider>
            ) : (
                chatbot
            )}
        </div>
    );

    return createPortal(sidebar, document.body);
};

/**
 * Panel shown near the top of the modal. It loads the first-pass
 * recommendations, then keeps the details collapsed until expanded.
 */
const SchedulingAssistant = ({
    overviewRequest,
}: {
    overviewRequest?: OverviewRequest;
}) => {
    const target = useSchedulingAssistantTarget();
    const conversation = useSchedulingAssistantConversation(
        target,
        overviewRequest?.activityId,
    );
    const { overview, estimatedTokensGenerated, connectionEstablished } =
        useInstructorOverview(
            target,
            overviewRequest,
            conversation.loaded,
            conversation.activityId,
            conversation.activeConversationId,
            conversation.setCreatedConversationId,
            conversation.restoredOverview,
            conversation.refreshVersion,
        );
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        setIsExpanded(false);
    }, [overviewRequest?.activityId, overview?.message]);

    if (!target || !overviewRequest?.activityId) return null;

    const generatedAt = formatGeneratedAt(conversation.generatedAt);

    return (
        <Box margin={{ bottom: 'm' }} data-testid="SchedulingAssistant">
            <ChatBubble
                type="incoming"
                stretch
                ariaLabel="Scheduling assistant recommendations preview"
                showLoadingBar={!overview}
                avatar={
                    <Button
                        variant="inline-link"
                        className="scheduling-assistant-avatar-button"
                        data-testid="SchedulingAssistantOpenButton"
                        onClick={() => setIsChatOpen(true)}
                        disabled={
                            !overview || !conversation.activeConversationId
                        }
                        ariaLabel="Open scheduling assistant chat"
                    >
                        <Avatar
                            color="gen-ai"
                            iconName="gen-ai"
                            loading={!overview}
                            ariaLabel="Scheduling assistant avatar"
                        />
                    </Button>
                }
            >
                <SpaceBetween direction="vertical" size="xxs">
                    {overview && (
                        <Box variant="small" color="text-body-secondary">
                            <SpaceBetween
                                direction="horizontal"
                                size="xs"
                                alignItems="center"
                            >
                                <Box
                                    variant="span"
                                    fontSize="body-s"
                                    color="text-body-secondary"
                                >
                                    {generatedAt
                                        ? `Generated ${generatedAt}`
                                        : 'Generated just now'}
                                </Box>
                                <Box
                                    display="inline-block"
                                    margin={{ top: 'xxs' }}
                                    padding={{ top: 'xxxs' }}
                                >
                                    <Button
                                        variant="inline-link"
                                        ariaLabel="Refresh recommendations"
                                        onClick={
                                            conversation.refreshConversation
                                        }
                                    >
                                        <Icon name="refresh" size="x-small" />
                                    </Button>
                                </Box>
                            </SpaceBetween>
                        </Box>
                    )}
                    <Box data-testid="SchedulingAssistantSummary">
                        {!overview ? (
                            <Box color="text-body-secondary">
                                {estimatedTokensGenerated > 0
                                    ? `Recommendations found! Generating overview (${estimatedTokensGenerated} tokens generated).`
                                    : connectionEstablished
                                      ? 'Searching for Instructor Recommendations'
                                      : 'Establishing connection'}
                            </Box>
                        ) : (
                            // Cloudscape Link without an href renders as a button.
                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                            <Link
                                variant="secondary"
                                className="scheduling-assistant-toggle-link"
                                onFollow={() => setIsExpanded(!isExpanded)}
                            >
                                {isExpanded
                                    ? 'Hide recommendations'
                                    : renderCollapsedSummary(overview)}
                            </Link>
                        )}
                    </Box>
                </SpaceBetween>
                {overview && isExpanded && (
                    <Box
                        margin={{ top: 's' }}
                        data-testid="SchedulingAssistantOverview"
                    >
                        <MarkdownText
                            text={withoutRecommendedInstructorsHeading(
                                overview.message,
                            )}
                        />
                        <Box margin={{ top: 's' }}>
                            <Button
                                variant="inline-link"
                                onClick={() => setIsChatOpen(true)}
                                disabled={!conversation.activeConversationId}
                            >
                                <Box variant="strong">
                                    Click here to chat about it!
                                </Box>
                            </Button>
                        </Box>
                    </Box>
                )}
            </ChatBubble>
            {isChatOpen && overview && conversation.activeConversationId && (
                <SchedulingAssistantSidebar
                    target={target}
                    activityId={overviewRequest?.activityId}
                    conversationId={conversation.activeConversationId}
                    onConversationIdChange={conversation.setConversationId}
                    onClose={() => setIsChatOpen(false)}
                    welcomeMessage={overview.message}
                />
            )}
        </Box>
    );
};

export default SchedulingAssistant;
